import { Dot } from '@/components/ui/dot';
import { Skeleton } from '@/components/ui/skeleton';
import { config } from '@/lib/config';
import { grafanaPromReady } from '@/lib/grafana/client';
import type { RtmServiceMetric } from '@/lib/mqtt/schemas/services';
import { useRtmStore } from '@/lib/store/rtm';
import { cn } from '@/lib/utils';
import { Fragment } from 'react';

/** Friendly group title from a k8s namespace (mockup §"PIPELINE CORE" etc.). */
function groupTitle(ns: string | undefined): string {
  if (!ns) return 'Other';
  return ns
    .replace(/^shared-/, '')
    .replace(/-/g, ' ')
    .toUpperCase();
}

/**
 * RTM Services table (mockup Panel 2). Full columns, grouped by namespace with
 * section headers, and a "total · healthy✓ · degraded⚠" count in the header.
 * Status filtering is driven by the topbar pills (useRtmStore.filters).
 */
export function RtmTable() {
  const byName = useRtmStore((s) => s.byName);
  const filters = useRtmStore((s) => s.filters);
  const selected = useRtmStore((s) => s.selectedName);
  const select = useRtmStore((s) => s.select);
  const services = Object.values(byName);
  // Initial-load shimmer: live mode + Grafana configured, store not yet filled.
  const loading = !config.isMock && grafanaPromReady() && services.length === 0;

  const healthy = services.filter((s) => s.pod_status === 'running').length;
  const degraded = services.filter((s) => s.pod_status === 'degraded').length;
  const offline = services.filter((s) => s.pod_status === 'down').length;

  const visible = services.filter((s) => {
    if (s.pod_status === 'running') return filters.healthy;
    if (s.pod_status === 'degraded') return filters.degraded;
    return filters.offline;
  });

  // Group visible services by namespace, groups + rows alpha-sorted.
  const groups = new Map<string, RtmServiceMetric[]>();
  for (const s of visible) {
    const key = groupTitle(s.namespace);
    (groups.get(key) ?? groups.set(key, []).get(key)!).push(s);
  }
  const groupKeys = [...groups.keys()].sort();

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <header className="flex items-center justify-between px-3 py-2 border-b border-(--color-border-lite) shrink-0">
        <div className="text-[0.692em] font-bold uppercase tracking-wider text-(--color-text3)">
          RTM Services
        </div>
        <div className="text-[0.692em] font-mono text-(--color-text3)">
          {services.length} · <span className="text-(--color-green)">{healthy}✓</span>
          {degraded > 0 && <span className="text-(--color-amber)"> · {degraded}⚠</span>}
          {offline > 0 && <span className="text-(--color-red)"> · {offline}✕</span>}
        </div>
      </header>
      <div className="flex-1 overflow-auto">
        <table className="w-full table-fixed text-[0.692em] font-mono">
          <thead className="sticky top-0 z-10 bg-(--color-surface) border-b border-(--color-border)">
            <tr className="text-(--color-text3) text-[0.615em] uppercase tracking-wider">
              <th className="text-left px-2 py-1.5 w-5"> </th>
              <th className="text-left px-2 py-1.5">Service</th>
              <th className="text-right px-2 py-1.5 w-14">RX</th>
              <th className="text-right px-2 py-1.5 w-14">TX</th>
              <th className="text-right px-2 py-1.5 w-10">↻</th>
              {/* Msg↓ / Msg↑ / Active omitted — no per-service metric mapped yet. */}
            </tr>
          </thead>
          <tbody>
            {loading &&
              ['l1', 'l2', 'l3', 'l4', 'l5', 'l6', 'l7', 'l8', 'l9', 'l10'].map((k) => (
                <tr key={k} className="border-b border-(--color-border-lite)">
                  <td colSpan={5} className="px-2 py-1">
                    <Skeleton className="h-3.5 w-full" />
                  </td>
                </tr>
              ))}
            {!loading && visible.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center py-6 text-(--color-text3)">
                  No services match filter.
                </td>
              </tr>
            )}
            {groupKeys.map((key) => (
              <Fragment key={key}>
                <tr>
                  <td
                    colSpan={5}
                    className="px-2 pt-3 pb-1 text-[0.538em] font-bold uppercase tracking-[0.1em] text-(--color-text3) bg-(--color-bg)"
                  >
                    {key}
                  </td>
                </tr>
                {groups
                  .get(key)!
                  .slice()
                  .sort((a, b) => a.service_name.localeCompare(b.service_name))
                  .map((s) => {
                    const tone =
                      s.pod_status === 'running'
                        ? 'ok'
                        : s.pod_status === 'degraded'
                          ? 'warn'
                          : 'crit';
                    return (
                      <tr
                        key={s.service_name}
                        onClick={() => select(s.service_name)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') select(s.service_name);
                        }}
                        tabIndex={0}
                        className={cn(
                          'cursor-pointer hover:bg-(--color-bg) transition-colors border-b border-(--color-border-lite)',
                          selected === s.service_name &&
                            'bg-(--color-green-bg) outline outline-2 outline-(--color-green) -outline-offset-2',
                        )}
                      >
                        <td className="px-2 py-1">
                          <Dot variant={tone} size="sm" />
                        </td>
                        <td className="px-2 py-1 text-(--color-text) truncate max-w-[160px]">
                          {s.service_name}
                        </td>
                        <td className="px-2 py-1 text-right">{s.rx?.toFixed(1) ?? '—'}</td>
                        <td className="px-2 py-1 text-right">{s.tx?.toFixed(1) ?? '—'}</td>
                        <td
                          className={cn(
                            'px-2 py-1 text-right',
                            s.restarts > 0
                              ? 'text-(--color-amber) font-bold'
                              : 'text-(--color-text3)',
                          )}
                        >
                          {s.restarts}
                        </td>
                      </tr>
                    );
                  })}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
